/**
 * THE ONLY CODE THAT MAY REFERENCE GOOGLE, AND THE ONLY CODE THAT MAY SEND.
 *
 * Nothing outside this module names googletagmanager, touches `dataLayer`, or
 * calls `gtag`. That is enforced by `tests/unit/book-analytics-isolation.test.js`, not by
 * convention, because "only the adapter talks to the vendor" is the kind of
 * rule that survives exactly until the first hurry.
 *
 * THE HARD PROPERTY: BEFORE AN AFFIRMATIVE ANSWER, NOTHING LOADS.
 *
 * Not "loads and stays quiet". The script element is never created,
 * `dataLayer` is never defined, and no request of any kind — including a
 * consent ping — is made. Google's consent mode DEFAULT command is
 * deliberately never sent: its whole design is that the library loads first
 * and decides afterwards, and a page that has contacted Google before the
 * visitor answered has already done the thing the question was about.
 *
 * CONSENT MODE IS USED, BUT ONLY AFTER A PERSISTED YES.
 *
 * The earlier version of this file rejected consent mode outright. That was
 * half right and Product's contract is the other half: once someone has said
 * yes and the answer is recorded, the library must be told what it may and may
 * not do, in Google's own vocabulary, BEFORE any configuration or event can
 * transmit. So the ordering is fixed and asserted:
 *
 *   1. load the library
 *   2. `consent` `update` — analytics granted, all three advertising
 *      categories denied
 *   3. `config`
 *   4. events
 *
 * Denying `ad_storage`, `ad_user_data` and `ad_personalization` in the command
 * as well as in GA Admin is not belt-and-braces for its own sake: only one of
 * those two places is in this repository, and the other can be changed by
 * someone who never sees this file.
 *
 * FOUR PHASES, AND ONLY ONE OF THEM IS PERMANENT.
 *
 *   idle         nothing loaded
 *   initialised  loaded, consent granted, events may send
 *   suspended    the visitor withdrew. Analytics storage is denied again and
 *                nothing sends — but the document can return to `initialised`
 *                if they change their mind, without a reload and without a
 *                second `config`.
 *   stopped      a credential route, or any gate failing. ONE-WAY. There is no
 *                restart, because the guarantee that matters there is not "we
 *                stopped sending" but "this document never had the library" —
 *                see docs/analytics/book-ga4-foundation.md §4 and
 *                `tests/unit/book-analytics-isolation.test.js`.
 *
 * Withdrawal is `suspended`, never `stopped`: conflating them would make an
 * ordinary preference change permanent for the page, and would mean a visitor
 * who declined and then reconsidered had to reload to be heard.
 */
import { syntheticPage, validateEvent, type EventName, type EventParams } from "./events";
import type { Category } from "./routes";

const LIBRARY_ORIGIN = "https://www.googletagmanager.com";

/**
 * What an affirmative answer actually permits, in Google's vocabulary.
 *
 * Analytics only. The three advertising categories are denied explicitly
 * rather than omitted — an omitted category is not a denied one, and this
 * surface has never asked anybody about advertising.
 */
const GRANTED_ANALYTICS_ONLY = {
  analytics_storage: "granted",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;

/** What withdrawal restores. Same four categories, nothing permitted. */
const DENIED_EVERYTHING = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;

type Phase = "idle" | "initialised" | "suspended" | "stopped";

interface Internals {
  phase: Phase;
  measurementId: string | null;
  /**
   * The host `initialise` configured, kept so `send` uses the SAME one.
   *
   * The first version let `initialise` take a host and had `send` reach for
   * the real `window`. In production they are the same object, so it worked —
   * and in a test it meant every `send` returned false because the real window
   * had no `dataLayer`. Three assertions passed for that reason instead of the
   * one they named, including "the adapter re-validates rather than trusting
   * its caller". A seam that only production takes is not a seam.
   */
  host: AdapterHost | null;
}

const internals: Internals = { phase: "idle", measurementId: null, host: null };

/** Test seam. Production always passes the real document. */
export interface AdapterHost {
  readonly document: Document;
  readonly window: Window & { dataLayer?: unknown[] };
}

function defaultHost(): AdapterHost | null {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  return { document, window: window as AdapterHost["window"] };
}

export function phase(): Phase {
  return internals.phase;
}

/**
 * Load and configure the library, exactly once.
 *
 * Returns false — and touches nothing — if it has already run or has been
 * stopped. The single-initialisation guard is a module latch rather than a
 * check for an existing script tag: React StrictMode double-invokes effects,
 * a re-render can re-run one, and two `config` calls for one measurement id
 * produce two sessions' worth of the same events.
 */
export function initialise(
  measurementId: string,
  category: Category,
  host: AdapterHost | null = defaultHost()
): boolean {
  if (!host) return false;
  if (internals.phase !== "idle") return false;

  /*
   * VALIDATE THE PAGE BEFORE LOADING ANYTHING.
   *
   * `send` re-validates on the boundary and `initialise` did not — it spread
   * `syntheticPage(category)` into the `config` call on nothing but a
   * TypeScript type. Review made that concrete: a classifier bug produced a
   * non-category `category`, and this was the step that turned a wrong return
   * value into a `config` payload carrying it as the document-wide default
   * page title. A category that cannot produce a valid page view must not be
   * allowed to load the library either.
   */
  const page = syntheticPage(category);
  if (!validateEvent("page_view", page).ok) return false;
  const target = host.window;
  target.dataLayer = target.dataLayer ?? [];
  const push = (...args: unknown[]) => {
    (target.dataLayer as unknown[]).push(args);
  };

  /*
   * CONSENT BEFORE CONFIGURATION. The order is the contract.
   *
   * `config` is what establishes the session, so a consent command that
   * arrived after it would be describing rules for something that had already
   * started. `dataLayer` is ordered, and `consentOrdering` in the tests pins
   * the index of this command below the index of `config`.
   */
  push("consent", "update", GRANTED_ANALYTICS_ONLY);
  push("js", new Date());
  push("config", measurementId, {
    // Manual, sanitised page views only. Without this the library sends one
    // for the current document as part of `config`, built from the real URL
    // and the real title.
    send_page_view: false,
    // Advertising and identity signals, off at the call site as well as in GA
    // Admin. Two places, because either one alone is a single point of
    // failure and only one of them is in this repository.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    allow_ad_user_data: false,
    // No User-ID, ever. Named explicitly so its absence is a decision in the
    // diff rather than an omission nobody notices.
    user_id: undefined,
    // Defaults for every subsequent event, so a hit that somehow omitted them
    // still could not carry the real page.
    ...page,
  });

  const script = host.document.createElement("script");
  script.async = true;
  script.src = `${LIBRARY_ORIGIN}/gtag/js?id=${encodeURIComponent(measurementId)}`;
  host.document.head.appendChild(script);

  internals.phase = "initialised";
  internals.measurementId = measurementId;
  internals.host = host;
  return true;
}

/**
 * Send one validated event, or refuse.
 *
 * Validation happens HERE as well as at the caller. The caller's check is for
 * developer feedback; this one is the boundary, and a boundary that trusts its
 * callers is decoration.
 */
export function send(name: EventName, params: EventParams = {}): boolean {
  if (internals.phase !== "initialised") return false;
  const checked = validateEvent(name, params);
  if (!checked.ok) return false;
  const host = internals.host;
  if (!host || !host.window.dataLayer) return false;
  host.window.dataLayer.push(["event", checked.name, checked.params]);
  return true;
}

/** A sanitised page view. The only page_view producer. */
export function sendPageView(category: Category): boolean {
  return send("page_view", syntheticPage(category));
}

/**
 * The visitor withdrew. Reversible, and it takes effect on this page.
 *
 * Returns analytics storage to denied through the library's own consent
 * command, so the library stops using it rather than merely stopping being
 * asked, and halts application events immediately. It does NOT unload
 * anything, because nothing can — and saying otherwise is how "analytics are
 * off" becomes a claim nobody checked.
 */
export function suspend(): void {
  if (internals.phase === "stopped") return;
  if (internals.phase === "initialised") {
    const host = internals.host;
    host?.window.dataLayer?.push(["consent", "update", DENIED_EVERYTHING]);
    internals.phase = "suspended";
  }
}

/**
 * They changed their mind, on the same page, without reloading.
 *
 * Re-grants through the consent command only. No second `config`: the session
 * already exists, and configuring twice for one measurement id produces two
 * sessions' worth of the same events. Refused from `stopped`, which is the
 * whole point of `stopped` being a different phase from `suspended`.
 */
export function resume(): boolean {
  if (internals.phase !== "suspended") return false;
  const host = internals.host;
  if (!host?.window.dataLayer) return false;
  host.window.dataLayer.push(["consent", "update", GRANTED_ANALYTICS_ONLY]);
  internals.phase = "initialised";
  return true;
}

/**
 * Stop, permanently, for this document.
 *
 * Deliberately does not attempt to remove the script element or delete
 * `dataLayer`: neither undoes a load, and code that pretends to would invite
 * the claim that a credential route is safe because analytics were "turned
 * off" on it. The exclusion that matters is that the library was never loaded
 * in that document at all.
 *
 * One way. `suspend`/`resume` are the reversible pair; this is not one of them.
 */
export function shutdown(): void {
  internals.phase = "stopped";
}

/** Test-only reset. Not exported from the package index. */
export function __resetForTests(): void {
  internals.phase = "idle";
  internals.measurementId = null;
  internals.host = null;
}
